import React from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

/**
 * ReciprocalGeoAlternate
 * ─────────────────────────────────────────────────────────────
 * Adds `<link rel="alternate" hreflang="..." href="...">` tags to the
 * DOCUMENT HEAD on Adelaide pages that have a matching Melbourne
 * sibling. Mounted once globally so we do not have to edit each of
 * the 5 Adelaide page components individually.
 *
 * Why this matters for SEO
 * ────────────────────────
 * Reciprocal geo signals help Google understand two URLs are
 * geo-variants of the same intent (not duplicates). The Melbourne
 * pages already link to their Adelaide sibling in-body; this closes
 * the loop from the Adelaide side without any visible UI change - the
 * signal is a machine-readable <link> element, invisible to users.
 *
 * SEO safety
 * ────────────────────────
 * - Uses `<link rel="alternate">` which is the W3C/Google-recommended
 *   pattern for geo-variants. Never `canonical` (that would deindex
 *   the other page).
 * - Does not touch <title>, <meta description>, <h1> or body copy.
 * - Adelaide pages keep self-canonical intact.
 */
const SIBLING_MAP = {
  '/first-home-buyer-adelaide': '/first-home-buyer-melbourne',
  '/adelaide-build-cost-report-2026': '/melbourne-build-cost-report-2026',
  '/home-builders-adelaide': '/home-builders-melbourne',
  '/knockdown-rebuild': '/knockdown-rebuild-melbourne',
  '/house-and-land-adelaide': '/house-and-land-melbourne',
  // Reverse - Melbourne → Adelaide (helmet-level reciprocal signal)
  '/first-home-buyer-melbourne': '/first-home-buyer-adelaide',
  '/melbourne-build-cost-report-2026': '/adelaide-build-cost-report-2026',
  '/home-builders-melbourne': '/home-builders-adelaide',
  '/knockdown-rebuild-melbourne': '/knockdown-rebuild',
  '/house-and-land-melbourne': '/house-and-land-adelaide',
  // Q&A pillars (VIC Phase 1, 2026-03-05) - SA ↔ VIC reciprocal
  '/how-much-does-it-cost-to-build-a-house-in-adelaide': '/how-much-does-it-cost-to-build-a-house-in-melbourne',
  '/how-much-does-it-cost-to-build-a-house-in-melbourne': '/how-much-does-it-cost-to-build-a-house-in-adelaide',
  '/how-long-does-it-take-to-build-a-house-in-adelaide': '/how-long-does-it-take-to-build-a-house-in-melbourne',
  '/how-long-does-it-take-to-build-a-house-in-melbourne': '/how-long-does-it-take-to-build-a-house-in-adelaide',
  '/is-it-cheaper-to-renovate-or-rebuild-adelaide': '/is-it-cheaper-to-renovate-or-rebuild-melbourne',
  '/is-it-cheaper-to-renovate-or-rebuild-melbourne': '/is-it-cheaper-to-renovate-or-rebuild-adelaide',
  '/what-is-hbcf-building-indemnity-insurance-sa': '/what-is-vba-domestic-building-insurance-vic',
  '/what-is-vba-domestic-building-insurance-vic': '/what-is-hbcf-building-indemnity-insurance-sa',
  '/what-council-approvals-do-i-need-adelaide': '/what-council-approvals-do-i-need-melbourne',
  '/what-council-approvals-do-i-need-melbourne': '/what-council-approvals-do-i-need-adelaide',
  '/how-much-does-it-cost-to-renovate-in-adelaide': '/how-much-does-it-cost-to-renovate-in-melbourne',
  '/how-much-does-it-cost-to-renovate-in-melbourne': '/how-much-does-it-cost-to-renovate-in-adelaide',
  '/how-do-i-choose-a-builder-in-adelaide': '/how-do-i-choose-a-builder-in-melbourne',
  '/how-do-i-choose-a-builder-in-melbourne': '/how-do-i-choose-a-builder-in-adelaide',
  '/what-is-a-knockdown-rebuild-adelaide': '/what-is-a-knockdown-rebuild-melbourne',
  '/what-is-a-knockdown-rebuild-melbourne': '/what-is-a-knockdown-rebuild-adelaide',
  '/how-much-deposit-do-i-need-for-a-new-build-adelaide': '/how-much-deposit-do-i-need-for-a-new-build-melbourne',
  '/how-much-deposit-do-i-need-for-a-new-build-melbourne': '/how-much-deposit-do-i-need-for-a-new-build-adelaide',
  '/best-time-of-year-to-build-in-adelaide': '/best-time-of-year-to-build-in-melbourne',
  '/best-time-of-year-to-build-in-melbourne': '/best-time-of-year-to-build-in-adelaide',
  // Comparison pages (VIC Phase 1, 2026-03-05)
  '/pergola-vs-verandah-adelaide': '/pergola-vs-verandah-melbourne',
  '/pergola-vs-verandah-melbourne': '/pergola-vs-verandah-adelaide',
  '/timber-vs-composite-decking-adelaide': '/timber-vs-composite-decking-melbourne',
  '/timber-vs-composite-decking-melbourne': '/timber-vs-composite-decking-adelaide',
  '/concrete-vs-fibreglass-pool-adelaide': '/concrete-vs-fibreglass-pool-melbourne',
  '/concrete-vs-fibreglass-pool-melbourne': '/concrete-vs-fibreglass-pool-adelaide',
  '/new-build-vs-knockdown-rebuild': '/knockdown-vs-renovate-melbourne',
  '/knockdown-vs-renovate-melbourne': '/new-build-vs-knockdown-rebuild',
  '/single-vs-double-storey': '/single-vs-double-storey-melbourne',
  '/single-vs-double-storey-melbourne': '/single-vs-double-storey',
  // Phase A final hubs (2026-03-05) - reciprocal SA ↔ VIC
  '/adelaide-display-home-directory': '/melbourne-display-home-directory',
  '/melbourne-display-home-directory': '/adelaide-display-home-directory',
  '/adelaide-building-market-report': '/melbourne-building-market-report',
  '/melbourne-building-market-report': '/adelaide-building-market-report',
  '/data/adelaide-home-building': '/data/melbourne-home-building',
  '/data/melbourne-home-building': '/data/adelaide-home-building',
};

const SITE = 'https://buildpilot.com.au';

const ReciprocalGeoAlternate = () => {
  const { pathname } = useLocation();
  const sibling = SIBLING_MAP[pathname];
  if (!sibling) return null;
  return (
    <Helmet>
      <link rel="alternate" hrefLang="en-AU" href={`${SITE}${sibling}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE}${sibling}`} />
    </Helmet>
  );
};

export default ReciprocalGeoAlternate;
